'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useState, type ReactNode } from 'react';

interface QueryProviderProps {
  children: ReactNode;
}

export function QueryProvider({ children }: QueryProviderProps) {
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          // Configurações otimizadas para o DroneOps
          staleTime: 1000 * 60 * 5, // 5 minutos
          gcTime: 1000 * 60 * 30,   // 30 minutos (antigo cacheTime)
          retry: (failureCount, error) => {
            // Não fazer retry em erros de autenticação
            if ((error as any)?.status === 401 || (error as any)?.status === 403) {
              return false;
            }
            return failureCount < 3;
          },
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          refetchOnReconnect: true,
        },
        mutations: {
          retry: false, // Não repetir mutations automaticamente
        },
      },
    });

    // 🔍 LOG: Monitorar invalidações de cache
    const originalInvalidateQueries = client.invalidateQueries;
    client.invalidateQueries = function(...args) {
      console.log('🔄 [QUERY_CLIENT] invalidateQueries called:', args);
      return originalInvalidateQueries.apply(this, args);
    };

    // 🔍 LOG: Monitorar atualizações de cache
    client.getQueryCache().subscribe((event) => {
      if (event?.type === 'updated') {
        console.log('📊 [QUERY_CACHE] Query updated:', {
          queryKey: event.query.queryKey,
          state: event.query.state.status,
          dataUpdatedAt: event.query.state.dataUpdatedAt,
          data: event.query.state.data ? Object.keys(event.query.state.data).length : 0
        });
      }
      if (event?.type === 'removed') {
        console.log('🗑️ [QUERY_CACHE] Query removed:', event.query.queryKey);
      }
    });

    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {process.env.NODE_ENV === 'development' && (
        <ReactQueryDevtools 
          initialIsOpen={false}
        />
      )}
    </QueryClientProvider>
  );
}